import * as React from 'react';

import './ShortcutCheatsheet.scss';

interface ShortcutItem {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  title: string;
  items: ShortcutItem[];
}

const detectMacLikePlatform = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const platform = navigator.platform ?? '';
  const userAgent = navigator.userAgent ?? '';
  return /mac|iphone|ipad|ipod/i.test(`${platform} ${userAgent}`);
};

const formatShortcutLabel = (key: string, isMacLike: boolean): string => {
  const normalized = key.replace(/Cmd\/Ctrl/g, isMacLike ? 'Cmd' : 'Ctrl');
  if (!isMacLike) {
    return normalized;
  }

  const macTokenMap: Record<string, string> = {
    Cmd: '⌘',
    Command: '⌘',
    Shift: '⇧',
    Alt: '⌥',
    Option: '⌥',
    Ctrl: '⌃',
    Control: '⌃',
    Enter: '↩',
    Return: '↩',
    Delete: '⌫',
    Backspace: '⌫',
    Esc: '⎋',
    Space: '␣',
  };
  const tokens = normalized.split(' + ').map(token => token.trim());
  const converted = tokens.map(token => macTokenMap[token] ?? token);
  const modifierSet = new Set(['⌘', '⇧', '⌥', '⌃']);
  const modifiers = converted.filter(token => modifierSet.has(token));
  const others = converted.filter(token => !modifierSet.has(token));
  const hasLongOrNonLatinKey = others.some(token => {
    if (token.length > 1) {
      return true;
    }
    for (const char of token) {
      if (char.charCodeAt(0) > 127) {
        return true;
      }
    }
    return false;
  });
  if (hasLongOrNonLatinKey) {
    return [...modifiers, ...others].join(' + ');
  }
  return [...modifiers, ...others].join('');
};

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: '全局操作',
    items: [
      { keys: ['Cmd/Ctrl + S'], description: '保存' },
      { keys: ['Cmd/Ctrl + Z'], description: '撤销' },
      { keys: ['Cmd/Ctrl + Shift + Z', 'Ctrl + Y'], description: '重做' },
      { keys: ['Cmd/Ctrl + A'], description: '全选' },
      { keys: ['Cmd/Ctrl + C'], description: '复制' },
      { keys: ['Cmd/Ctrl + V'], description: '粘贴' },
      { keys: ['Cmd/Ctrl + X'], description: '剪切' },
      { keys: ['Cmd/Ctrl + D'], description: '克隆' },
      { keys: ['Cmd/Ctrl + G'], description: '组合' },
      { keys: ['Cmd/Ctrl + Shift + G'], description: '取消组合' },
      { keys: ['Delete', 'Backspace'], description: '删除选中元素' },
      { keys: ['Esc'], description: '取消当前操作 / 退出到选择模式' },
      { keys: ['?'], description: '打开快捷键速查' },
      { keys: ['0'], description: '重置视口' },
    ],
  },
  {
    title: '工具切换',
    items: [
      { keys: ['V'], description: '钢笔工具' },
      { keys: ['P'], description: '铅笔工具' },
      { keys: ['R'], description: '矩形工具' },
      { keys: ['O'], description: '椭圆工具' },
      { keys: ['Y'], description: '三角形工具' },
      { keys: ['T'], description: '文本工具' },
      { keys: ['I'], description: '导入图片' },
    ],
  },
  {
    title: '文本编辑',
    items: [
      { keys: ['Cmd/Ctrl + Enter'], description: '提交文本' },
      { keys: ['Esc'], description: '结束文本编辑' },
    ],
  },
  {
    title: '状态特例',
    items: [
      { keys: ['Alt + 框选拖拽'], description: '触碰到元素即可选中（相交选中）' },
      { keys: ['Shift + 角点拖拽缩放'], description: '等比例缩放（锁定宽高比）' },
      { keys: ['Esc'], description: '在钢笔绘制中结束路径（PenState）' },
      { keys: ['Esc'], description: '退出形状编辑（ShapeEditState）' },
      { keys: ['Delete'], description: '删除当前锚点（ShapeEditState）' },
    ],
  },
];

interface ShortcutCheatsheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShortcutCheatsheet: React.FC<ShortcutCheatsheetProps> = ({ isOpen, onClose }) => {
  const isMacLike = React.useMemo(() => detectMacLikePlatform(), []);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="shortcut-cheatsheet-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="shortcut-cheatsheet-modal"
        onClick={event => {
          event.stopPropagation();
        }}
        role="dialog"
        aria-modal="true"
        aria-label="快捷键速查"
      >
        <div className="shortcut-cheatsheet-header">
          <h2>快捷键速查</h2>
          <button type="button" onClick={onClose} aria-label="关闭快捷键速查">
            关闭
          </button>
        </div>
        <div className="shortcut-cheatsheet-content">
          {SHORTCUT_GROUPS.map(group => (
            <section key={group.title} className="shortcut-cheatsheet-group">
              <h3>{group.title}</h3>
              <ul>
                {group.items.map(item => (
                  <li key={`${group.title}-${item.keys.join('/')}-${item.description}`}>
                    <div className="shortcut-cheatsheet-keys">
                      {item.keys.map(rawKey => {
                        const key = formatShortcutLabel(rawKey, isMacLike);
                        return (
                          <kbd key={`${group.title}-${item.description}-${rawKey}`}>{key}</kbd>
                        );
                      })}
                    </div>
                    <span>{item.description}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <p className="shortcut-cheatsheet-note">
          当前显示为{isMacLike ? ' macOS（⌘/⇧/⌥/⌃）' : ' Windows/Linux '}键位。当焦点位于输入框或文本域时，应用级快捷键不会触发。
        </p>
      </div>
    </div>
  );
};

export default ShortcutCheatsheet;
